import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { TIERS, priceOf, FEATURE_ICONS } from "@/data/tiers";

export default function MembershipPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="membership-page">
      <Seo
        title={t("vc.seo.membership.title")}
        description={t("vc.seo.membership.desc")}
        schema={[
          breadcrumbSchema([{ name: "VangardClub", path: "/" }, { name: "Membership", path: "/membership" }]),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "VangardClub Membership",
            description: t("vc.seo.membership.desc"),
            brand: { "@type": "Brand", name: "VangardClub" },
            offers: [
              { "@type": "Offer", name: "Silver", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
              { "@type": "Offer", name: "Gold", price: "3000", priceCurrency: "USD", availability: "https://schema.org/LimitedAvailability" },
              { "@type": "Offer", name: "VIP", price: "7000", priceCurrency: "USD", availability: "https://schema.org/LimitedAvailability" },
              { "@type": "Offer", name: "Corporate", priceCurrency: "USD", availability: "https://schema.org/PreOrder" },
            ],
          },
        ]}
      />
      <VangardHeader />

      <section className="relative overflow-hidden pt-[112px] lg:pt-[68px]">
        <VMark position="top-right" size={620} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-28">
          <div className="text-[10px] tracking-[0.4em] uppercase text-neutral-500">{t("vc.tiers.eyebrow")}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 max-w-[20ch] leading-[1.05]">
            {t("vc.tiers.title")}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[58ch]">
            {t("vc.tiers.sub")}
          </p>

          <div className="mt-10 lg:mt-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-5">
            {TIERS.map((tier) => (
              <div
                key={tier.id}
                data-testid={`tier-card-${tier.id}`}
                className={`relative rounded-3xl p-6 lg:p-7 flex flex-col border bg-[#0A0A0A] ${
                  tier.popular ? "border-white/45 shadow-[0_0_40px_rgba(255,255,255,0.05)]" : "border-white/[0.08]"
                }`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-6 bg-white text-black px-3.5 py-1 rounded-full text-[9px] tracking-[0.25em] uppercase font-medium">
                    {t("vc.tiers.most-popular")}
                  </span>
                )}
                <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t(tier.tag)}</div>
                <h2 className="font-serif text-3xl mt-3">{tier.name}</h2>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-xl">{priceOf(tier, t)}</span>
                  {tier.period && <span className="text-[11px] text-neutral-500">{t(tier.period)}</span>}
                </div>
                <p className="mt-3.5 text-[13px] text-neutral-400 font-light leading-relaxed">{t(tier.body)}</p>

                <div className="my-5 h-px bg-white/[0.08]" />

                <ul className="space-y-2.5 flex-1">
                  {tier.features.map((f) => {
                    const Icon = FEATURE_ICONS[f] || Check;
                    const accent = FEATURE_ICONS[f] ? "text-[#E5484D]" : "text-white/50";
                    return (
                      <li key={f} className="flex items-start gap-2 text-[12px] text-neutral-300 font-light leading-relaxed">
                        <Icon size={13} strokeWidth={1.75} className={`mt-0.5 flex-shrink-0 ${accent}`} />
                        {t(f)}
                      </li>
                    );
                  })}
                </ul>

                <button
                  onClick={() => navigate(user ? "/account/membership" : "/register")}
                  data-testid={`tier-select-${tier.id}`}
                  className={`mt-6 inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase transition-colors ${
                    tier.popular
                      ? "bg-white text-black hover:bg-neutral-200"
                      : "border border-white/20 text-white hover:bg-white/5 hover:border-white/45"
                  }`}
                >
                  {tier.id === "corporate" ? t("vc.tiers.contact") : tier.id === "silver" ? t("vc.tiers.get-started") : t("vc.tiers.select")}
                  <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A] p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="max-w-xl">
              <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.mem.bespoke-eyebrow")}</div>
              <div className="font-serif text-xl mt-2">{t("vc.mem.bespoke-title-1")} {t("vc.mem.bespoke-title-2")}</div>
              <p className="text-[13px] text-neutral-400 font-light mt-2 leading-relaxed">{t("vc.mem.bespoke-body")}</p>
            </div>
            <button
              onClick={() => navigate(user ? "/account/concierge" : "/register")}
              className="shrink-0 inline-flex items-center justify-center bg-white text-black px-6 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
              data-testid="membership-contact"
            >
              {t("vc.tiers.contact")}
            </button>
          </div>
        </div>
      </section>

      <VangardFooter />
    </div>
  );
}
